import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import HotmailDeliveryNotice from '../components/HotmailDeliveryNotice';
import CaptchaWidget from '../components/CaptchaWidget';
import './Auth.css';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  // Tier 3 Item 26 — pick up ?ref=CODE and persist across navigation
  useEffect(() => {
    const fromUrl = searchParams.get('ref');
    if (fromUrl) {
      const code = fromUrl.trim().toUpperCase();
      setReferralCode(code);
      try { localStorage.setItem('referral_code', code); } catch (_) {}
    } else {
      try {
        const saved = localStorage.getItem('referral_code');
        if (saved) setReferralCode(saved);
      } catch (_) {}
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/users/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          referral_code: referralCode || undefined,
          captcha_token: captchaToken || undefined,
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.dispatchEvent(new Event('auth-change'));
        // New accounts are 'pending' approval — push them straight into the
        // application form. Server sends `redirect: '/apply'` on success.
        navigate(data.redirect || (data.user?.approval_status === 'pending' ? '/apply' : '/account'));
      } else {
        setError(data.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Create Your Account</h1>
        <p className="auth-subtitle">Join the SilverGames community and start collecting!</p>

        <div role="note" style={{
          marginBottom: 16, padding: 12,
          background: '#eef2ff', border: '1px solid #c7d2fe',
          borderRadius: 6, fontSize: 13, color: '#1e3a8a',
        }}>
          <strong>One quick step:</strong> after you register, you'll fill out a short application
          (business name, address, how you'll use SilverGames). New accounts are typically reviewed
          and approved within <strong>72 hours</strong> — pricing and checkout unlock once approved.
        </div>

        {error && (
          <div className="error" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} aria-label="Registration form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">
                First Name <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">
                Last Name <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required" aria-label="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              aria-required="true"
              autoComplete="email"
            />
            <HotmailDeliveryNotice email={formData.email} />
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
              aria-describedby="phone-help"
            />
            <small id="phone-help" className="form-help">
              Optional - We'll only use this for order updates
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password <span className="required" aria-label="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              aria-required="true"
              autoComplete="new-password"
              aria-describedby="password-requirements"
            />
            <small id="password-requirements" className="form-help">
              Must be at least 8 characters long
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              Confirm Password <span className="required" aria-label="required">*</span>
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              aria-required="true"
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="referralCode">
              Referral code <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>(optional)</span>
            </label>
            <input
              type="text"
              id="referralCode"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase().trim())}
              placeholder="e.g. ABCD2345"
              style={{ textTransform: 'uppercase' }}
              autoComplete="off"
            />
          </div>

          <CaptchaWidget onToken={setCaptchaToken} />

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account?{' '}
          <Link to="/login">Sign in here</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
